import { Outlet, useLocation } from 'react-router-dom';
import { CardSessionProvider } from '../ticket/cardSessionContext';
import { useState, useEffect } from 'react';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { useShortcut } from '../../lib/keyboard';
import styles from './Shell.module.css';
import { cn } from '@vd/ui';
import { CommandPalette } from '../modals/CommandPalette';
import { KeyboardShortcutsModal } from '../modals/KeyboardShortcutsModal';
import { BannerQueue } from '../banners/BannerQueue';

const SIDEBAR_KEY = 'vd-sidebar-collapsed';
// At tablet and below, an expanded 240px sidebar squeezes content into
// the leftover ~500-600px and looked broken on HomePage (the welcome
// card dropped below an oversized nav strip). Force-collapse at this
// breakpoint so the sidebar renders icon-only regardless of what the
// user persisted on desktop. The match query stays in sync with the
// matching CSS rule in Shell.module.css so the visual width and the
// React-side render mode never disagree.
const FORCE_COLLAPSE_QUERY = '(max-width: 900px)';

export function Shell() {
  const [persistedCollapsed, setPersistedCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_KEY) === '1',
  );
  // Mirror the media query into React state so the Sidebar component
  // picks up the icon-only layout at the same width the CSS shrinks
  // the grid track. Falsy default + an effect sync ensures SSR / static
  // hydration land on a stable value before the listener mounts.
  const [forceCollapse, setForceCollapse] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(FORCE_COLLAPSE_QUERY).matches;
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, persistedCollapsed ? '1' : '0');
  }, [persistedCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(FORCE_COLLAPSE_QUERY);
    const update = (e: MediaQueryListEvent | MediaQueryList) => setForceCollapse(e.matches);
    update(mql);
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // At small widths the sidebar stays an icon strip, and opening it shows
  // the full sidebar as a drawer over the page (TOADO-825). The open
  // button used to write the saved preference, which the forced collapse
  // overrode, so at that size it did nothing. The drawer is not saved:
  // it closes on navigation, Escape or a click outside, and on widening.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, forceCollapse]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    const onPointer = (e: PointerEvent) => {
      if (!(e.target instanceof Element) || !e.target.closest('aside[aria-label="Primary"]')) setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [drawerOpen]);

  // Effective collapsed: the saved preference, or at small widths the
  // drawer's state. The `[` toggle drives whichever applies, so widening
  // the viewport restores the user's choice automatically.
  const collapsed = forceCollapse ? !drawerOpen : persistedCollapsed;
  const toggleSidebar = () => {
    if (forceCollapse) setDrawerOpen((open) => !open);
    else setPersistedCollapsed((c) => !c);
  };

  useShortcut('[', toggleSidebar, { page: true });
  useShortcut('?', () => setShortcutsOpen(true), { page: true });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={cn(styles.shell, collapsed && styles.collapsed)}>
      <BannerQueue />
      <TopBar onOpenPalette={() => setPaletteOpen(true)} />
      <div className={styles.body}>
        <Sidebar collapsed={collapsed} drawer={forceCollapse && drawerOpen} onToggle={toggleSidebar} />
        <main className={styles.main}>
          {/* Any card's Session button opens its window over the page (TOADO-945). */}
          <CardSessionProvider>
            <Outlet />
          </CardSessionProvider>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
